// library functions

import { TimelineMax } from "gsap/TimelineMax";
import { TweenLite } from "gsap/TweenLite";
import { TweenMax } from "gsap/TweenMaxBase";

// hlf stuff
import * as HLF from "html-living-framework";
import type { IHiddenItemConfig } from "Interfaces/IHiddenItemConfig";
import type { ILevelState } from "Interfaces/ILevelState";
import type { IVoiceoverConfig } from "Interfaces/IVoiceoverConfig";

// static data
import * as Constants from "Data/Constants";
import { GameConfig } from "Data/GameConfig";
import type { ItemID, LocationID } from "Data/GameConfig";
import * as VO from "Data/VO";

// pubsub topics
import * as Topics from "Data/Topics";

// gameobjects
import { InteriorItemsListGiveQuest } from "GameObjects/GameInterior/GiveQuest/InteriorItemsListGiveQuest";
import { InteriorLunaGiveQuest } from "GameObjects/GameInterior/GiveQuest/InteriorLunaGiveQuest";
import { InteriorNPCGiveQuest } from "GameObjects/GameInterior/GiveQuest/InteriorNPCGiveQuest";
import { InteriorBackground } from "GameObjects/GameInterior/InteriorBackground";
import { InteriorHUD } from "GameObjects/GameInterior/InteriorHUD";

// singletons
import { Application } from "Application";
import { addBG as addBG, addVO } from "Utils/AssetUtils";

// #region private constants
const fnt = Constants.FONT_GEL_MED_22;
const ss = Constants.SPRITESHEET_UI;
const STATE_GAME_INTERIOR: string = "State_GameInteriorGiveQuest";
const IDLE_DELAY = 10;
const IDLE_REPEAT_DELAY = 10;
// #endregion

export class GameInteriorGiveQuest extends HLF.State {

// #region static properties
    public static readonly Assets: HLF.AssetMapData[] = [
        new HLF.AssetMapData(fnt, HLF.AssetType.FONT),
        new HLF.SpineAsset("luna", HLF.AssetType.SPINE_JSON, "luna"),
        ...ss.map((s) => new HLF.AssetMapData(s, HLF.AssetType.TEXTURE_ATLAS)),
        new HLF.AudioAsset("item-give", HLF.AudioCategory.SFX),
    ];
//#endregion

// #region children
    protected bg = this.addChild(new InteriorBackground());
    protected hud = this.addChild(new InteriorHUD());
    protected itemsList = this.addChild(new InteriorItemsListGiveQuest());
    protected luna = this.addChild(new InteriorLunaGiveQuest());
    protected npc = this.addChild(new InteriorNPCGiveQuest());
// #endregion

// #region local state
    protected _subscriptions: any[] = [];
    protected vo?: IVoiceoverConfig;
    protected bgm?: HLF.IAudioTrack;
    protected idleTimeline?: TimelineMax;
    protected _wasContinue: boolean = false;
// #endregion

// #region temporary or debug variables
// #endregion

// #region static methods
    public static GetIdForState(id: LocationID, narrative: number, variation: number) {
        return `${STATE_GAME_INTERIOR}_${id}_${narrative}_${variation}`;
    }

    public static GetAssetsForState(pID: LocationID, narrative: number, variation: number): HLF.AssetMapData[] {
        const locationConfig = GameConfig.locations[pID];
        const narrativeConfig = locationConfig.narratives[narrative];
        const narrativeVariationConfig = narrativeConfig.variations[variation];

        const interior = locationConfig.interior;
        const vo = {...narrativeConfig.vo, ...narrativeVariationConfig.vo, ...locationConfig.vo};
        const hiddenitems = narrativeVariationConfig.hiddenitems as IHiddenItemConfig[];
        const itemIDs: ItemID[] = hiddenitems.map((h) => (typeof(h.item) === "string" ? h.item : h.item.id) as ItemID);

        const assets: HLF.AssetMapData[]  = [];

        // VO
        addVO(assets, vo.give_quest);
        addVO(assets, VO.UI.GIVE.O2);
        addVO(assets, vo.show_items);
        itemIDs.forEach((id: ItemID, i) => {
            const item = locationConfig.items[id];
            addVO(assets, item.vo?.returned);
        });
        // Background
        addBG(pID, assets, interior.bg, HLF.AssetType.JPG);
        // music
        assets.push(new HLF.AudioAsset(locationConfig.bgm, HLF.AudioCategory.BGM));

        // Spritesheet contains Hiding Spots, Hidden Items, and NPC
        assets.push(
            ...HLF.AssetFactory(GameConfig.spritesheet_factory[pID], (i) => new HLF.TextureAtlasAsset(`${pID}-${i}`)));

        return assets;
    }
// #endregion

    public init(pSize: PIXI.Point, pData: ILevelState & {firstTime: boolean, wasContinue: boolean}) {
        super.init(pSize, pData); // this calls onResize

        PubSub.publishSync(HLF.PUSH_KEYBOARD_LAYER, undefined);

        this.setChildIndex(this.bg,         0);
        this.setChildIndex(this.itemsList,  1);
        this.setChildIndex(this.npc,        2);
        this.setChildIndex(this.luna,       3);
        this.setChildIndex(this.hud,        4);

        this.bg.init(pSize, pData);
        this.itemsList.init(pSize, pData);
        this.luna.init(pSize, pData);
        this.npc.init(pSize, pData);
        this.hud.init(pSize, pData);

        this.initVoiceover(pData);
        this.initMusic(pData);

        this._wasContinue = pData.wasContinue;

        this._subscriptions.push(
            PubSub.subscribe(Topics.RESTART_IDLE_TIMER, this.RestartIdleTimer.bind(this)),
            PubSub.subscribe(HLF.GAME_PAUSED, () => this.idleTimeline?.kill()),
            PubSub.subscribe(HLF.GAME_UNPAUSED, this.RestartIdleTimer.bind(this)),
        );

        // console.log("receive quest: ", pData);
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
        this.bg.onResize(pSize);
        this.itemsList.onResize(pSize);
        this.hud.onResize(pSize);
        this.npc.onResize(pSize);
        this.luna.onResize(pSize);
    }

    public animateIn(pOnComplete: () => void) {
        this.bg.animateIn();
        this.npc.animateIn();
        this.luna.animateIn();
        this.hud.animateIn();
        this.itemsList.animateIn();

        this.bgm?.setLooped(true);
        this.bgm?.setVolume(0.125);
        if (!(this.bgm?.isPlaying())) {
            this.bgm?.play();
        }

        super.animateIn(pOnComplete);

        const showContinueButton = () => {
            PubSub.publishSync(Topics.SHOW_CONTINUE_BUTTON, {allItemsFound: false});
            // this.luna.StopInstructing();
            TweenMax.delayedCall(2, () => {
                Application.instance.voiceover.playVO([VO.UI.GIVE.O2, Constants.VO_SPACING], HLF.PlayMode.New);
            });
        };

        const showItems = () => {
            this.luna.Instructs();
            if (this.vo?.show_items === undefined || Application.instance.skipVO) {
                showContinueButton();
            }
            else {
                Application.instance.voiceover.playVO(
                    [Constants.VO_SPACING, ...this.vo.show_items],
                    HLF.PlayMode.Append,
                    () => { showContinueButton(); },
                );
            }
        };

        TweenMax.delayedCall(1, () => {
            if (this.vo?.give_quest === undefined || this.vo?.show_items === undefined || Application.instance.skipVO) {
                showContinueButton();
            }
            else {
                Application.instance.voiceover.playVO(this.vo.give_quest,  HLF.PlayMode.Append, showItems);
            }
        });

        this.idleTimeline = new TimelineMax();
        this.idleTimeline.delay(IDLE_DELAY);
        if (this.vo?.give_quest_idle !== undefined && this.vo?.give_quest_idle.length === 2) {
            this.vo.give_quest_idle.splice(1, 0, VO.UI.GIVE.O2);
            this.vo.give_quest_idle.push(VO.UI.GIVE.O2);
        }
        this.idleTimeline.call(() => {
            if (this.vo?.give_quest_idle !== undefined) {
                this.idleTimeline?.kill();
                const voToPlay = this.vo.give_quest_idle.shift()!;
                if (voToPlay === VO.UI.GIVE.O2) {
                    PubSub.publishSync(Topics.SHOW_CONTINUE_BUTTON, {allItemsFound: false});
                }
                Application.instance.voiceover.playVO(
                    [voToPlay, Constants.VO_SPACING],
                    HLF.PlayMode.New,
                    (pDidPlay) => {
                        if (pDidPlay) {
                            this.vo!.give_quest_idle!.push(voToPlay);
                        }
                        else {
                            this.vo!.give_quest_idle!.unshift(voToPlay);
                        }
                        this.RestartIdleTimer();
                    },
                );
            }
        }, undefined, undefined, IDLE_REPEAT_DELAY);
        this.idleTimeline.repeat(-1);
    }

    public update(pDeltaTime: number): void {
        super.update(pDeltaTime);
        this.luna.update(pDeltaTime);
    }

    public animateOut(pOnComplete: () => void) {
        super.animateOut(pOnComplete);
        PubSub.publishSync(HLF.POP_KEYBOARD_LAYER, undefined);
    }

    public destroy(pOptions?: PIXI.DestroyOptions | boolean) {
        this._subscriptions.forEach(PubSub.unsubscribe);
        TweenLite.killTweensOf(this);
        this.idleTimeline?.kill();
        super.destroy(pOptions);
    }

    protected RestartIdleTimer() {
        this.idleTimeline?.restart(true, true);
    }

    protected initVoiceover(state: ILevelState) {
        const locationConfig = GameConfig.locations[state.id];
        const narrativeConfig = GameConfig.locations[state.id].narratives[state.narrative];
        const narrativeVariationConfig =
            GameConfig.locations[state.id].narratives[state.narrative].variations[state.narrativeVariation];

        this.vo = {...locationConfig.vo, ...narrativeConfig.vo, ...narrativeVariationConfig.vo};

        // console.log(this.vo);
    }

    protected initMusic(state: ILevelState) {
        const locationConfig = GameConfig.locations[state.id];
        this.bgm = Application.instance.audio.getAudioTrack(locationConfig.bgm, HLF.AudioCategory.BGM);
    }
}
